import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, CheckCircle2, Save, ExternalLink, Wind, Heart, Move, Zap, Lightbulb, Info, ArrowLeft, Dumbbell, Plus, Trash2 } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooter from '@/components/website/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/utils/constants';
import { useIsPwaMode } from '@/hooks/useIsPwaMode';
import Navigation from '@/components/Navigation';

// ==================== Exercise / Advice Catalog ====================
// Each item: { id, label, description, path (in-app link) }
// Categories match the therapist's mental model.
// The "bolt-test" item is auto-injected at the top of the "mecanique" section for every user.
// It cannot be unchecked from the creation page (it is always required).
export const REQUIRED_BOLT_ITEM = {
  id: 'bolt-test',
  label: 'Tester mon score BOLT',
  description: 'Mesure de référence de tolérance au CO₂. À faire chaque semaine, le matin à jeun.',
  path: '/breathing/bolt',
  required: true,
};

export const CATALOG = {
  mecanique: [
    // 🎥 Videos pédagogiques — playlist "Respiration"
    { id: 'video-etages-respiratoires', label: 'Vidéo : les étages respiratoires', description: 'Comprendre les 3 étages (abdominal, thoracique, claviculaire) et leur coordination.', href: 'https://www.youtube.com/watch?v=npENP7-rOOs&list=PLM1Xo_tNlIgTNzJAyzs8WFyYdiqiVlCom&index=2', external: true },
    { id: 'video-perception-sensations', label: 'Vidéo : perception des sensations internes', description: 'Développer l\'interoception : écouter les signaux fins du corps.', href: 'https://www.youtube.com/watch?v=7KyaR7rZiPM&list=PLM1Xo_tNlIgTNzJAyzs8WFyYdiqiVlCom&index=4', external: true },
    { id: 'video-mecanique-optimale', label: 'Vidéo : la mécanique respiratoire optimale', description: 'Diaphragme, côtes, posture — les bases d\'une respiration efficace.', href: 'https://www.youtube.com/watch?v=WAJkNpz1Lg0&list=PLM1Xo_tNlIgTNzJAyzs8WFyYdiqiVlCom&index=9', external: true },
    { id: 'video-desengager-epaules', label: 'Vidéo : désengager les épaules', description: 'Technique pour libérer les épaules des efforts respiratoires parasites.', href: 'https://www.youtube.com/watch?v=q4YCmb4SAGo&list=PLM1Xo_tNlIgTNzJAyzs8WFyYdiqiVlCom&index=10', external: true },
    // 📖 Lectures de référence sur le site
    { id: 'mecanique-respiratoire', label: 'Lecture : la mécanique respiratoire', description: 'Comprendre les mouvements diaphragmatiques et costaux.', href: 'https://physiomainsnuages.ch/mecanique-respiratoire', external: true },
    { id: 'respiration-optimale', label: 'Lecture : la respiration optimale', description: 'Piliers d\'une respiration saine (nez, diaphragme, silence).', href: 'https://physiomainsnuages.ch/respiration-optimale', external: true },
  ],
  biochimie: [
    // 🎯 Test BOLT toujours prescrit — mesure de tolérance au CO2
    REQUIRED_BOLT_ITEM,
    // Tous les exercices de désensibilisation au CO2
    // Note: paths target ?exercise=X / ?challenge=X query params so that
    // BreathingAppPage auto-opens the right modal on arrival (one-click
    // workflow for elderly patients — no sub-navigation).
    { id: 'buteyko', label: 'Petites apnées répétées (Buteyko)', description: 'Réduit la ventilation, augmente la tolérance au CO₂.', path: '/breathing/practice?exercise=buteyko' },
    { id: 'reduire-frequence', label: 'Respiration réduite', description: 'Diminuer sa fréquence respiratoire à 6-8 cycles/min.', path: '/breathing/practice?exercise=reduire-frequence' },
    { id: 'respiration-carree', label: 'Respiration carrée (Box breathing)', description: 'Rééquilibre le rythme respiratoire (4-4-4-4).', path: '/breathing/practice?exercise=box' },
    { id: 'narines-alternees', label: 'Narines alternées', description: 'Rééquilibre les deux voies nasales.', path: '/breathing/practice?exercise=alternate-nostril' },
    { id: 'levres-pincees-co2', label: 'Focus sur la capacité à expirer : Allongement du temps expiratoire', description: 'Prolonge l\'expiration, améliore la tolérance au CO₂.', path: '/breathing/practice?exercise=pursed-lip' },
  ],
  nerf_vague: [
    // ==== Exercices guidés de l'app (section "Calmante / Nerf Vague") ====
    { id: 'coherence', label: 'Cohérence cardiaque 5-5', description: 'Active le système parasympathique (5 min, 3×/jour).', path: '/breathing/practice?exercise=coherence' },
    { id: '478', label: 'Technique 4-7-8', description: 'Endormissement, réduction rapide du stress.', path: '/breathing/practice?exercise=4-7-8' },
    { id: 'levres-pincees', label: 'Focus sur la capacité à expirer : Allongement du temps expiratoire', description: 'Prolonge l\'expiration, calme le rythme.', path: '/breathing/practice?exercise=pursed-lip' },
    { id: 'calmante', label: 'Respiration calmante', description: 'Guide pas à pas pour redescendre en pression.', path: '/breathing/practice?exercise=calming' },
    { id: 'russian', label: 'Maîtres russes', description: 'Séquence longue et lente pour ancrer la détente profonde.', path: '/breathing/practice?exercise=russian-masters' },
    { id: 'haptique', label: 'Respiration haptique (mobile)', description: 'Guidage tactile par vibrations pour poser le souffle.', path: '/breathing/practice?exercise=haptic' },
    { id: 'narines-alternees-vague', label: 'Narines alternées (Nadi Shodhana)', description: 'Rééquilibre sympathique/parasympathique.', path: '/breathing/practice?exercise=alternate-nostril' },
    { id: 'reduire-frequence-vague', label: 'Respiration réduite (6 c/min)', description: 'Ralentir la fréquence stimule le tonus vagal.', path: '/breathing/practice?exercise=reduire-frequence' },

    // ==== Techniques respiratoires à pratiquer hors app ====
    { id: 'soupir-physio', label: 'Double soupir physiologique', description: '2 inspirations nasales rapides + 1 longue expiration bouche. 1-3 cycles pour calmer instantanément.' },
    { id: 'bhramari', label: 'Bhramari (respiration de l\'abeille)', description: 'Inspiration nasale + expiration en bourdonnement doux, mâchoire relâchée. 5-10 cycles.' },
    { id: 'ujjayi', label: 'Ujjayi (respiration océanique)', description: 'Léger resserrement de la glotte à l\'inspir et à l\'expir : son océanique, effet apaisant profond.' },
    { id: 'trois-parties', label: 'Respiration en 3 parties (Dirgha)', description: 'Remplir ventre → côtes → clavicules, puis vider dans l\'ordre inverse. 5-10 cycles.' },
    { id: 'demi-sourire', label: 'Demi-sourire + expir lent', description: 'Détend la mâchoire et le nerf vague ventral. À poser dès qu\'on remarque une tension.' },
    { id: 'gargarisme', label: 'Gargarismes vigoureux', description: 'Stimule le nerf vague via les muscles laryngés. 30 s par jour à l\'eau tiède.' },
    { id: 'chant', label: 'Chanter / vocaliser (om, aaa)', description: 'Vibrations vocales prolongées = stimulation vagale directe.' },

    // ==== Rituels & habitudes ====
    { id: 'eau-froide-reveil', label: 'Au réveil : eau froide sur le visage et le cou', description: 'Stimulation du nerf vague par le réflexe de plongée mammifère.' },
    { id: 'humming', label: 'Humming (fredonnement)', description: 'Vibrations laryngées qui activent le nerf vague, plusieurs fois dans la journée.' },
    { id: 'rosenberg', label: 'Exercice de base de Rosenberg', description: 'Mobilisation cervicale douce (regard) pour rééquilibrer le tronc cérébral.' },
    { id: 'ecoute-respiratoire', label: 'Écoute respiratoire', description: 'S\'arrêter, poser l\'attention sur le souffle sans le modifier.' },
    { id: 'expirer', label: 'Expirer !', description: 'Rappel : allonger consciemment l\'expiration dès que le stress monte.' },
    { id: 'socialiser', label: 'Socialiser', description: 'Le lien social est le premier régulateur du nerf vague ventral.' },
    { id: 'danser', label: 'Danser', description: 'Mouvement rythmique + plaisir : co-régulation somatique et émotionnelle.' },
    { id: 'jouer', label: 'Jouer', description: 'Activité ludique quotidienne pour sortir du mode défense.' },
    { id: 'coherence-cardiaque-page', label: 'Lecture : cohérence cardiaque', description: 'Bases scientifiques et mise en pratique.', path: '/coherence-cardiaque' },
  ],
  mobilite: [
    { id: 'mobilite-diaphragme', label: 'Auto-massage du diaphragme', description: 'Sous les côtes, en respirant profondément.', path: '/exercices' },
    { id: 'mobilite-cage-thoracique', label: 'Étirements de la cage thoracique', description: 'Latéral, torsion, ouverture — 3×30 s.', path: '/exercices' },
    { id: 'mobilite-cervicales', label: 'Mobilisation cervicale douce', description: 'Détend les scalènes et les trapèzes.', path: '/exercices' },
    { id: 'mobilite-epaules', label: 'Ouverture des épaules', description: 'Contre la fermeture posturale liée au stress.', path: '/exercices' },
    { id: 'mobilite-mur', label: 'Mobilité au mur', description: 'Séquence d\'ouverture thoracique et scapulaire debout contre un mur.' },
    { id: 'salutation-soleil', label: 'Salutation au soleil', description: 'Séquence Sûrya Namaskar : mobilité globale + respiration coordonnée.' },
    { id: 'mobilite-baton', label: 'Mobilité avec bâton', description: 'Circumductions, passages devant/derrière : gagne en amplitude scapulaire.' },
    { id: 'qi-gong-8', label: '8 Mouvements de Qi gong', description: 'Enchaînement doux qui associe mobilité, respiration et attention.' },
    { id: 'qi-gong-6', label: 'Qi Gong 6 Mouvements', description: 'Version courte : 6 mouvements pour ouvrir la respiration et détendre le corps.' },
  ],
  conseils: [
    { id: 'respiration-nasale', label: 'Respirer par le nez, jour et nuit', description: 'Filtration, humidification, meilleure oxygénation.', path: '/respiration-optimale' },
    { id: 'moins-respirer', label: 'Moins respirer pour mieux respirer', description: 'Volume courant réduit → meilleure tolérance CO₂.', path: '/methode-buteyko' },
    { id: 'sommeil', label: 'Hygiène du sommeil', description: 'Chambre fraîche, tape buccale, éviter écrans avant coucher.', path: '/exercices' },
    { id: 'posture', label: 'Vérifier la posture', description: 'Buste ouvert, épaules basses, menton légèrement rentré.', path: '/mecanique-respiratoire' },
    { id: 'hydratation', label: 'Hydratation régulière', description: 'Fluidifie les sécrétions, aide la mécanique respiratoire.', path: '/exercices' },
  ],
};

export const SECTION_META = {
  mecanique: { title: 'Mécanique respiratoire', icon: Wind, gradient: 'from-sky-500 to-cyan-500', color: 'text-sky-700', border: 'border-sky-200', bg: 'bg-sky-50' },
  biochimie: { title: 'Biochimie : se désensibiliser au CO₂', icon: Zap, gradient: 'from-amber-500 to-rose-500', color: 'text-rose-700', border: 'border-rose-200', bg: 'bg-rose-50' },
  nerf_vague: { title: 'Activation du nerf vague', icon: Heart, gradient: 'from-emerald-500 to-teal-500', color: 'text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50' },
  mobilite: { title: 'Exercices de mobilité', icon: Move, gradient: 'from-amber-500 to-orange-500', color: 'text-amber-700', border: 'border-amber-200', bg: 'bg-amber-50' },
  conseils: { title: 'Conseils du thérapeute', icon: Lightbulb, gradient: 'from-violet-500 to-purple-500', color: 'text-violet-700', border: 'border-violet-200', bg: 'bg-violet-50' },
};

const FIELD_MAP = {
  mecanique: 'mecanique_items',
  biochimie: 'biochimie_items',
  nerf_vague: 'nerf_vague_items',
  mobilite: 'mobilite_items',
  conseils: 'conseils_items',
};

const WeeklyProgramPage = ({ targetUserId = null, onBack = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminEditing = !!targetUserId;
  // When the admin is editing another patient, read & write go through the
  // dedicated admin endpoints (no need to login as the patient). Otherwise
  // the current flow (self-service edit while patient is logged in) is kept.
  const programUrl = isAdminEditing
    ? `${API}/admin/users/${targetUserId}/weekly-program`
    : `${API}/weekly-program`;
  const recordsUrl = isAdminEditing
    ? `${API}/admin/users/${targetUserId}/challenges/records`
    : `${API}/challenges/records`;
  const [editingUser, setEditingUser] = useState(null); // {name,email} when admin
  const [sendingEmail, setSendingEmail] = useState(false);
  const isPwa = useIsPwaMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState({
    bilan_biochimique: { bolt: '', inspir_max: '', expir_max: '', apnea_inspir: '', mbt: '', explication: '' },
    bilan_mecanique: '',
    bilan_nerveux: { status: '', notes: '' },
    mecanique_items: [],
    biochimie_items: [],
    nerf_vague_items: [],
    mobilite_items: [],
    conseils_items: [],
    reinforcement: { enabled: false, reps_per_day: 30, notes: '' },
    therapist_notes: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch both the saved program AND the user's latest challenge records in parallel.
        const [progResp, recordsResp] = await Promise.all([
          axios.get(programUrl),
          axios.get(recordsUrl).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        // Admin endpoint wraps the program in {program, user}; the self one returns it flat.
        const raw = progResp.data || {};
        const data = isAdminEditing ? (raw.program || {}) : raw;
        if (isAdminEditing && raw.user) setEditingUser(raw.user);
        const rec = recordsResp?.data || {};
        // Auto-fill helper: use saved value if therapist already typed one, otherwise
        // reuse the last record from the patient's history.
        const autofill = (savedVal, key, unit) => {
          if (savedVal != null && String(savedVal).trim() !== '') return savedVal;
          const record = rec?.[key];
          if (!record) return '';
          const raw = unit === 'pas' ? (record.steps ?? record.duration_seconds) : record.duration_seconds;
          if (raw == null) return '';
          return String(Math.round(Number(raw)));
        };
        setProgram({
          bilan_biochimique: {
            bolt:         autofill(data.bilan_biochimique?.bolt,         'hold'),
            inspir_max:   autofill(data.bilan_biochimique?.inspir_max,   'inhale'),
            expir_max:    autofill(data.bilan_biochimique?.expir_max,    'exhale'),
            apnea_inspir: autofill(data.bilan_biochimique?.apnea_inspir, 'apnea'),
            mbt:          autofill(data.bilan_biochimique?.mbt,          'mbt', 'pas'),
            explication: data.bilan_biochimique?.explication || '',
          },
          bilan_mecanique: data.bilan_mecanique || '',
          bilan_nerveux: {
            status: data.bilan_nerveux?.status || '',
            notes: data.bilan_nerveux?.notes || '',
          },
          mecanique_items: Array.isArray(data.mecanique_items) ? data.mecanique_items : [],
          biochimie_items: Array.isArray(data.biochimie_items) ? data.biochimie_items : [],
          nerf_vague_items: Array.isArray(data.nerf_vague_items) ? data.nerf_vague_items : [],
          mobilite_items: Array.isArray(data.mobilite_items) ? data.mobilite_items : [],
          conseils_items: Array.isArray(data.conseils_items) ? data.conseils_items : [],
          reinforcement: {
            enabled: !!data.reinforcement?.enabled,
            reps_per_day: Number(data.reinforcement?.reps_per_day) || 30,
            notes: data.reinforcement?.notes || '',
          },
          therapist_notes: data.therapist_notes || '',
        });
      } catch (err) {
        console.error('Failed to load weekly program', err);
        toast.error('Impossible de charger le programme');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getItem = (sectionKey, id) => {
    const field = FIELD_MAP[sectionKey];
    return program[field].find((i) => i.id === id) || null;
  };

  const isItemRequired = (sectionKey, id) => {
    const item = (CATALOG[sectionKey] || []).find((i) => i.id === id);
    return !!(item && item.required);
  };

  const toggleItem = (sectionKey, id) => {
    if (isItemRequired(sectionKey, id)) {
      toast.info('Cet exercice est obligatoire pour tous les patients.');
      return;
    }
    const field = FIELD_MAP[sectionKey];
    setProgram((prev) => {
      const items = [...(prev[field] || [])];
      const idx = items.findIndex((i) => i.id === id);
      if (idx >= 0) {
        items[idx] = { ...items[idx], checked: !items[idx].checked };
      } else {
        items.push({ id, checked: true, note: null });
      }
      return { ...prev, [field]: items };
    });
  };

  const updateItemNote = (sectionKey, id, note) => {
    const field = FIELD_MAP[sectionKey];
    setProgram((prev) => {
      const items = [...(prev[field] || [])];
      const idx = items.findIndex((i) => i.id === id);
      if (idx >= 0) {
        items[idx] = { ...items[idx], note };
      } else {
        items.push({ id, checked: false, note });
      }
      return { ...prev, [field]: items };
    });
  };

  // ---- Custom (therapist-added) instructions per section ----
  // Sections that support custom instructions ("mecanique", "biochimie", "nerf_vague").
  const CUSTOM_SUPPORTED = new Set(['mecanique', 'biochimie', 'nerf_vague']);

  const addCustomItem = (sectionKey) => {
    const field = FIELD_MAP[sectionKey];
    setProgram((prev) => ({
      ...prev,
      [field]: [
        ...(prev[field] || []),
        { id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, checked: true, custom: true, label: '', note: null },
      ],
    }));
  };

  const updateCustomLabel = (sectionKey, id, label) => {
    const field = FIELD_MAP[sectionKey];
    setProgram((prev) => {
      const items = (prev[field] || []).map((i) => (i.id === id ? { ...i, label, custom: true, checked: true } : i));
      return { ...prev, [field]: items };
    });
  };

  const removeCustomItem = (sectionKey, id) => {
    const field = FIELD_MAP[sectionKey];
    setProgram((prev) => ({ ...prev, [field]: (prev[field] || []).filter((i) => i.id !== id) }));
  };

  const saveProgram = async () => {
    setSaving(true);
    try {
      const clean = (arr, sectionKey) => {
        const kept = (arr || []).filter((i) => {
          if (!i) return false;
          if (i.custom) return (i.label && i.label.trim()) || (i.note && i.note.trim());
          return i.checked || (i.note && i.note.trim());
        });
        // Force-inject every required item as checked so it always shows in "Mon programme".
        (CATALOG[sectionKey] || []).filter((c) => c.required).forEach((c) => {
          if (!kept.find((k) => k.id === c.id)) {
            kept.unshift({ id: c.id, checked: true, note: null });
          } else {
            const idx = kept.findIndex((k) => k.id === c.id);
            kept[idx] = { ...kept[idx], checked: true };
          }
        });
        return kept;
      };
      await axios.put(programUrl, {
        bilan_biochimique: program.bilan_biochimique,
        bilan_mecanique: program.bilan_mecanique || null,
        bilan_nerveux: program.bilan_nerveux,
        mecanique_items: clean(program.mecanique_items, 'mecanique'),
        biochimie_items: clean(program.biochimie_items, 'biochimie'),
        nerf_vague_items: clean(program.nerf_vague_items, 'nerf_vague'),
        mobilite_items: clean(program.mobilite_items, 'mobilite'),
        conseils_items: clean(program.conseils_items, 'conseils'),
        reinforcement: program.reinforcement,
        therapist_notes: program.therapist_notes || null,
      });
      toast.success('Programme enregistré');
    } catch (err) {
      console.error('Save failed', err);
      toast.error("Impossible d'enregistrer le programme");
    } finally {
      setSaving(false);
    }
  };

  // Admin-only: send the program by email to the patient (short recap + share link).
  const sendProgramByEmail = async () => {
    if (!isAdminEditing) return;
    if (!window.confirm(`Envoyer le programme à ${editingUser?.email || 'ce patient'} ?`)) return;
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/${targetUserId}/send-weekly-program`);
      toast.success('Email envoyé avec le lien de partage');
      // Expose the share link so the admin can copy it or send it another way.
      console.info('Share link:', data?.share_link);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSendingEmail(false);
    }
  };

  const totalSelected = useMemo(() => {
    return ['mecanique_items', 'biochimie_items', 'nerf_vague_items', 'mobilite_items', 'conseils_items']
      .reduce((sum, k) => sum + (program[k] || []).filter((i) => i.checked).length, 0);
  }, [program]);

  const renderSection = (sectionKey) => {
    const meta = SECTION_META[sectionKey];
    const items = CATALOG[sectionKey];
    const Icon = meta.icon;
    const field = FIELD_MAP[sectionKey];
    const customItems = (program[field] || []).filter((i) => i && i.custom);
    const supportsCustom = CUSTOM_SUPPORTED.has(sectionKey);
    return (
      <section key={sectionKey} className={`rounded-2xl border-2 ${meta.border} ${meta.bg} p-4 sm:p-6 shadow-sm`} data-testid={`weekly-section-${sectionKey}`}>
        <header className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${meta.gradient} shadow-md`}>
            <Icon size={20} className="text-white" strokeWidth={2.2} />
          </div>
          <h2 className={`text-xl sm:text-2xl font-semibold ${meta.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {meta.title}
          </h2>
        </header>
        <ul className="space-y-2">
          {items.map((it) => {
            const state = getItem(sectionKey, it.id);
            const isRequired = !!it.required;
            const checked = isRequired ? true : !!state?.checked;
            const note = state?.note || '';
            return (
              <li key={it.id} className={`rounded-xl border p-3 transition-all ${checked ? 'bg-white border-slate-300 shadow-sm' : 'bg-white/60 border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => toggleItem(sectionKey, it.id)}
                    disabled={isRequired}
                    className={`mt-0.5 w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center transition-all ${checked ? `bg-gradient-to-br ${meta.gradient} text-white shadow` : 'bg-white border-2 border-slate-300 hover:border-slate-500'} ${isRequired ? 'cursor-not-allowed opacity-90' : ''}`}
                    aria-label={isRequired ? 'Exercice obligatoire' : (checked ? 'Décocher' : 'Cocher')}
                    title={isRequired ? 'Obligatoire pour tous les patients' : ''}
                    data-testid={`weekly-check-${it.id}`}
                  >
                    {checked && <CheckCircle2 size={16} strokeWidth={2.5} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <p className={`font-medium ${checked ? 'text-slate-900' : 'text-slate-700'}`}>
                        {it.label}
                        {isRequired && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-sky-800 bg-sky-100 border border-sky-200 rounded-full px-2 py-0.5 align-middle">
                            Obligatoire
                          </span>
                        )}
                      </p>
                      {(it.path || it.href) && (
                        <button
                          type="button"
                          onClick={() => {
                            if (it.href) window.open(it.href, '_blank', 'noopener,noreferrer');
                            else navigate(it.path);
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-sky-700 transition"
                          data-testid={`weekly-launch-${it.id}`}
                        >
                          <ExternalLink size={12} />
                          Ouvrir
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>
                    {checked && (
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => updateItemNote(sectionKey, it.id, e.target.value)}
                        placeholder="Note du thérapeute (ex: 2×/jour avant le repas)"
                        className="mt-2 w-full px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
                        data-testid={`weekly-note-${it.id}`}
                      />
                    )}
                  </div>
                </div>
              </li>
            );
          })}

          {/* Therapist-added custom instructions */}
          {customItems.map((ci) => (
            <li key={ci.id} className={`rounded-xl border p-3 bg-white border-dashed ${meta.border}`}>
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${meta.gradient} text-white shadow`}>
                  <CheckCircle2 size={16} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start gap-2">
                    <input
                      type="text"
                      value={ci.label || ''}
                      onChange={(e) => updateCustomLabel(sectionKey, ci.id, e.target.value)}
                      placeholder="Nouvelle consigne personnalisée…"
                      className="flex-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400"
                      data-testid={`weekly-custom-label-${ci.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomItem(sectionKey, ci.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                      aria-label="Supprimer cette consigne"
                      data-testid={`weekly-custom-remove-${ci.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={ci.note || ''}
                    onChange={(e) => updateItemNote(sectionKey, ci.id, e.target.value)}
                    placeholder="Détail / fréquence (facultatif)"
                    className="w-full px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
                    data-testid={`weekly-custom-note-${ci.id}`}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>

        {supportsCustom && (
          <button
            type="button"
            onClick={() => addCustomItem(sectionKey)}
            className={`mt-3 inline-flex items-center gap-1.5 text-sm font-semibold ${meta.color} hover:underline`}
            data-testid={`weekly-add-custom-${sectionKey}`}
          >
            <Plus size={16} /> Ajouter une consigne personnalisée
          </button>
        )}
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50">
      {!isPwa && !isAdminEditing && <SiteHeaderV2 />}

      {isAdminEditing && (
        /* Admin banner — makes it obvious we're editing someone else's program. */
        <div className="sticky top-0 z-40 bg-amber-500 text-white px-4 py-2 shadow-md flex items-center justify-between gap-3" data-testid="admin-editing-banner">
          <button
            type="button"
            onClick={() => (onBack ? onBack() : navigate(-1))}
            className="inline-flex items-center gap-1 text-white/95 hover:text-white font-medium text-sm"
          >
            <ArrowLeft size={16} /> Retour admin
          </button>
          <div className="text-sm font-semibold text-center flex-1 truncate">
            Édition programme : {editingUser?.name || '…'}
            {editingUser?.email && <span className="opacity-80 font-normal"> ({editingUser.email})</span>}
          </div>
          <div className="w-24" aria-hidden />
        </div>
      )}

      <main className={`${isPwa ? 'pt-8' : (isAdminEditing ? 'pt-6' : 'pt-24')} pb-16 max-w-4xl mx-auto px-4 sm:px-6`} data-testid="weekly-program-page">
        {/* Header */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate('/mon-espace')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-3"
            data-testid="back-to-mon-espace"
          >
            <ArrowLeft size={14} /> Retour à Mon espace
          </button>
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <ClipboardList size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Création de programme
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Interface thérapeute : cochez les exercices à prescrire cette semaine et ajoutez des notes. Le patient les retrouvera en lecture seule dans « Mon programme ».
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-600 shadow-sm">
              <UserBadge name={user?.name} />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 border border-sky-200 text-sm text-sky-700">
              <CheckCircle2 size={14} /> {totalSelected} exercice{totalSelected > 1 ? 's' : ''} prescrit{totalSelected > 1 ? 's' : ''}
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-200 text-sm text-amber-800">
              <Info size={14} /> Cliquez &quot;Ouvrir&quot; pour lancer un exercice
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Chargement…</div>
        ) : (
          <div className="space-y-6">
            {/* ===== Synthèse du bilan ===== */}
            <section className="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-4 sm:p-6 shadow-sm" data-testid="weekly-section-bilan">
              <header className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-blue-600 shadow-md">
                  <Info size={20} className="text-white" strokeWidth={2.2} />
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-indigo-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Synthèse du bilan
                </h2>
              </header>

              {/* Bloc 1 – Biochimique */}
              <div className="bg-white rounded-xl border border-indigo-200 p-4 mb-4">
                <h3 className="text-sm font-bold text-indigo-800 uppercase tracking-wide mb-3">1. Aspect biochimique</h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { key: 'bolt', label: 'BOLT', unit: 's', placeholder: 'ex. 18' },
                    { key: 'inspir_max', label: 'Inspir max', unit: 's', placeholder: 'ex. 25' },
                    { key: 'expir_max', label: 'Expir max', unit: 's', placeholder: 'ex. 30' },
                    { key: 'apnea_inspir', label: 'Apnée inspir', unit: 's', placeholder: 'ex. 42' },
                    { key: 'mbt', label: 'MBT', unit: 'pas', placeholder: 'ex. 60' },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                        {f.label}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={program.bilan_biochimique[f.key] || ''}
                          onChange={(e) => setProgram((p) => ({ ...p, bilan_biochimique: { ...p.bilan_biochimique, [f.key]: e.target.value } }))}
                          placeholder={f.placeholder}
                          className="w-full pl-2 pr-8 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          data-testid={`bilan-bio-${f.key}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{f.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                    Explication & conséquences (intolérance au CO₂, essoufflement…)
                  </label>
                  <textarea
                    rows={3}
                    value={program.bilan_biochimique.explication || ''}
                    onChange={(e) => setProgram((p) => ({ ...p, bilan_biochimique: { ...p.bilan_biochimique, explication: e.target.value } }))}
                    placeholder="Un BOLT inférieur à 25 s indique une hypersensibilité au CO₂ : essoufflement rapide à l'effort, respiration buccale, hyperventilation chronique…"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    data-testid="bilan-bio-explication"
                  />
                </div>
              </div>

              {/* Bloc 2 – Mécanique (texte libre) */}
              <div className="bg-white rounded-xl border border-indigo-200 p-4 mb-4">
                <h3 className="text-sm font-bold text-indigo-800 uppercase tracking-wide mb-2">2. Aspect mécanique</h3>
                <p className="text-xs text-slate-500 mb-2">Une observation par ligne (posture, respiration paradoxale, blocages, latéralité…).</p>
                <textarea
                  rows={5}
                  value={program.bilan_mecanique}
                  onChange={(e) => setProgram((p) => ({ ...p, bilan_mecanique: e.target.value }))}
                  placeholder={`- Respiration paradoxale à l'inspir\n- Absence d'expansion costale basse\n- Épaules hautes et enroulées\n- Tension diaphragmatique à la palpation`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400 leading-relaxed"
                  data-testid="bilan-mecanique"
                />
              </div>

              {/* Bloc 3 – Système nerveux */}
              <div className="bg-white rounded-xl border border-indigo-200 p-4">
                <h3 className="text-sm font-bold text-indigo-800 uppercase tracking-wide mb-3">3. Système nerveux</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                  {[
                    { key: 'equilibre', label: 'Équilibré', desc: 'Bon tonus vagal, régulation efficace', tone: 'emerald' },
                    { key: 'stress_fuite_combat', label: 'Stress · fuite / combat', desc: 'Sympathique dominant : irritabilité, hyperventilation, tachycardie', tone: 'rose' },
                    { key: 'stress_inhibition', label: 'Stress · inhibition / immobilisation', desc: 'Vague dorsal dominant : fatigue, sidération, retrait', tone: 'slate' },
                  ].map((opt) => {
                    const active = program.bilan_nerveux.status === opt.key;
                    const toneMap = {
                      emerald: active ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200' : 'bg-white border-slate-200 hover:border-emerald-300',
                      rose: active ? 'bg-rose-50 border-rose-400 ring-2 ring-rose-200' : 'bg-white border-slate-200 hover:border-rose-300',
                      slate: active ? 'bg-slate-100 border-slate-500 ring-2 ring-slate-200' : 'bg-white border-slate-200 hover:border-slate-400',
                    };
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setProgram((p) => ({ ...p, bilan_nerveux: { ...p.bilan_nerveux, status: p.bilan_nerveux.status === opt.key ? '' : opt.key } }))}
                        className={`text-left rounded-xl border-2 p-3 transition-all ${toneMap[opt.tone]}`}
                        data-testid={`bilan-nerveux-${opt.key}`}
                      >
                        <p className="font-semibold text-slate-800 text-sm">{opt.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{opt.desc}</p>
                      </button>
                    );
                  })}
                </div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                  Notes complémentaires (facultatif)
                </label>
                <textarea
                  rows={2}
                  value={program.bilan_nerveux.notes || ''}
                  onChange={(e) => setProgram((p) => ({ ...p, bilan_nerveux: { ...p.bilan_nerveux, notes: e.target.value } }))}
                  placeholder="Ex : bascule fréquente entre les deux formes de stress selon le contexte…"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="bilan-nerveux-notes"
                />
              </div>
            </section>

            {['mecanique', 'biochimie', 'nerf_vague', 'mobilite', 'conseils'].map((sectionKey, idx) => (
              <div key={`wrap-${sectionKey}`}>
                {idx === 0 && (
                  <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="program-heading">
                    Mon programme
                  </h2>
                )}
                {renderSection(sectionKey)}
              </div>
            ))}

            {/* Respiratory muscle reinforcement */}
            <section className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-4 sm:p-6 shadow-sm" data-testid="weekly-section-reinforcement">
              <header className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 shadow-md">
                  <Dumbbell size={20} className="text-white" strokeWidth={2.2} />
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-rose-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Renforcement de la musculature respiratoire
                </h2>
              </header>
              <label className="flex items-start gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={program.reinforcement.enabled}
                  onChange={(e) => setProgram((p) => ({ ...p, reinforcement: { ...p.reinforcement, enabled: e.target.checked } }))}
                  className="mt-1 w-5 h-5 rounded accent-rose-600"
                  data-testid="weekly-reinforcement-toggle"
                />
                <div>
                  <p className="font-medium text-slate-900">Programme quotidien avec appareil de renforcement respiratoire</p>
                  <p className="text-xs text-slate-500 mt-0.5">Type POWERbreathe, Airofit, ou équivalent. À utiliser matin et/ou soir.</p>
                </div>
              </label>
              {program.reinforcement.enabled && (
                <div className="ml-8 space-y-3 bg-white rounded-xl p-4 border border-rose-200">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Répétitions par jour</label>
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={program.reinforcement.reps_per_day}
                      onChange={(e) => setProgram((p) => ({ ...p, reinforcement: { ...p.reinforcement, reps_per_day: Number(e.target.value) || 0 } }))}
                      className="w-32 px-3 py-2 rounded-lg border border-slate-300 text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
                      data-testid="weekly-reinforcement-reps"
                    />
                    <span className="ml-2 text-sm text-slate-600">répétitions / jour</span>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Notes / niveau de résistance</label>
                    <textarea
                      rows={2}
                      value={program.reinforcement.notes || ''}
                      onChange={(e) => setProgram((p) => ({ ...p, reinforcement: { ...p.reinforcement, notes: e.target.value } }))}
                      placeholder="Ex: niveau 3 sur POWERbreathe Plus, 2 séries de 15"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-400"
                      data-testid="weekly-reinforcement-notes"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/entrainement-muscles-respiratoires')}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 hover:text-rose-900"
                  >
                    <ExternalLink size={12} /> En savoir plus sur l&apos;entraînement des muscles respiratoires
                  </button>
                </div>
              )}
            </section>

            {/* Global therapist notes */}
            <section className="rounded-2xl border-2 border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              <header className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-slate-500 to-slate-700 shadow-md">
                  <ClipboardList size={20} className="text-white" strokeWidth={2.2} />
                </div>
                <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Notes générales du thérapeute
                </h2>
              </header>
              <textarea
                rows={4}
                value={program.therapist_notes || ''}
                onChange={(e) => setProgram((p) => ({ ...p, therapist_notes: e.target.value }))}
                placeholder="Objectifs de la semaine, contexte particulier, points de vigilance…"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
                data-testid="weekly-therapist-notes"
              />
            </section>

            {/* Save button */}
            <div className="pb-16 sm:pb-20" />
            <div className="sticky bottom-4 z-30 flex flex-wrap justify-end gap-2">
              {isAdminEditing && (
                <button
                  type="button"
                  onClick={sendProgramByEmail}
                  disabled={sendingEmail || saving}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/95 border-2 border-sky-500 text-sky-700 font-semibold shadow-lg hover:bg-sky-50 transition-all disabled:opacity-60"
                  data-testid="weekly-send-email-btn"
                >
                  {sendingEmail ? 'Envoi…' : (<><ExternalLink size={16} /> Envoyer par email</>)}
                </button>
              )}
              <button
                type="button"
                onClick={saveProgram}
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-sky-600 to-cyan-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
                data-testid="weekly-save-btn"
              >
                {saving ? 'Enregistrement…' : (<><Save size={16} /> Enregistrer le programme</>)}
              </button>
            </div>
          </div>
        )}
      </main>

      {isPwa && !isAdminEditing ? (
        <>
          <div style={{ height: 'calc(env(safe-area-inset-bottom,0px) + 100px)' }} aria-hidden="true" />
          <Navigation />
        </>
      ) : !isAdminEditing ? (
        <SiteFooter />
      ) : null}
    </div>
  );
};

const UserBadge = ({ name }) => (
  <span className="inline-flex items-center gap-1.5">
    <Zap size={14} className="text-amber-500" />
    <span className="font-medium">{name || 'Patient'}</span>
  </span>
);

export default WeeklyProgramPage;
